import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "reactstrap";
import { MdDeleteOutline } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";
import Cookies from "js-cookie";

import {
  deleteCourseRequest,
  updateCourseRequest,
  fetchCoursesRequest
} from "store/actions";

import ConfirmationModal from "components/Modals/ConfirmationModal";
import CreateCourse from "./CreateCourse";
import { formatISOToDDMMYYYY } from "helpers/dateFormat_helper";
import CourseDataTable from "components/TableContainers/CourseDataTable";

const CourseTable = ({ courses, loading, totalrows }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(false);
  const [selectedSortData, setSelectedSortData] = useState({
    value: "createdAt",
    direction: "desc"
  });
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchString, setSearchString] = useState("");

  const permissions = JSON.parse(localStorage.getItem("permissions"));
  const hasEditPermission = permissions?.some(
    (item) => item?.permission_name === "Edit Course"
  );
  const hasDeletePermission = permissions?.some(
    (item) => item?.permission_name === "Delete Course"
  );

  const handleDeleteConfirmed = (id) => {
    dispatch(deleteCourseRequest(id));
    setOpenModal(false);
    setConfirmAction(false);
  };

  useEffect(() => {
    if (deleteId && confirmAction) {
      handleDeleteConfirmed(deleteId);
    }
  }, [confirmAction]);

  // Columns
  const columns = useMemo(
    () => [
{
  header: "Title",
  accessorKey: "title",
  cell: ({ row }) => (
    <div
      style={{
        maxWidth: "250px",
        whiteSpace: "normal",   // ✅ allows text to wrap
        overflow: "hidden",
        textOverflow: "ellipsis",
        wordWrap: "break-word",
        lineHeight: "1.4",
        display: "-webkit-box",
        WebkitLineClamp: 2,     // ✅ show only 2 lines max
        WebkitBoxOrient: "vertical",
        color: "#222",
      }}
      title={row.original.title} // full text on hover
    >
      {row.original.title || "-"}
    </div>
  ),
},
{
  header: "Description",
  accessorKey: "description",
  cell: ({ row }) => (
    <div
      style={{
        maxWidth: "400px",
        whiteSpace: "normal",
        overflow: "hidden",
        textOverflow: "ellipsis",
        wordWrap: "break-word",
        lineHeight: "1.5",
        display: "-webkit-box",
        WebkitLineClamp: 3,      
        WebkitBoxOrient: "vertical",
        color: "#555",
      }}
      title={row.original.description}
    >
      {row.original.description || "-"}
    </div>
  ),
},

      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => (
          <span>{row.original.status ? "Active" : "Inactive"}</span>
        )
      },
      {
        header: "Created At",
        accessorKey: "createdAt",
        cell: ({ row }) => (
          <span>{formatISOToDDMMYYYY(row.original.createdAt)}</span>
        )
      },
      ...(hasEditPermission || hasDeletePermission
        ? [
            {
              header: "Actions",
              id: "actions",
              cell: ({ row }) => {
                const handleEdit = () => {
                  setEditData(row.original);
                  setIsOpen(true);
                };

                const handleDelete = () => {
                  setDeleteId(row.original._id);
                  setOpenModal(true);
                };

                return (
                  <div className="d-flex gap-2">
                    {hasEditPermission && (
                      <Button
                        color="primary"
                        title="Edit"
                        onClick={handleEdit}
                      >
                        <FaRegEdit size={18} />
                      </Button>
                    )}
                    {hasDeletePermission && (
                      <Button
                        color="danger"
                        title="Delete"
                        onClick={handleDelete}
                      >
                        <MdDeleteOutline size={18} />
                      </Button>
                    )}
                  </div>
                );
              }
            }
          ]
        : [])
    ],
    [hasEditPermission, hasDeletePermission]
  );

  const handleFormSubmit = (id, data, onClose) => {
    dispatch(updateCourseRequest(id, data, onClose));
  };

  return (
    <>
      {/* Confirmation Modal */}
      <ConfirmationModal
        okText="Confirm"
        onCancel={() => setOpenModal(false)}
        onOk={() => setConfirmAction(true)}
        isVisible={openModal}
        title="Delete Course"
        content="Are you sure you want to delete this course?"
      />

      {/* Edit Modal */}
      <CreateCourse
        visible={isOpen}
        initialData={editData}
        onSubmit={handleFormSubmit}
        handleClose={() => setIsOpen(false)}
      />

      {/* Data Table */}
      <div className="container-fluid">
        <CourseDataTable
          columns={columns}
          loading={loading}
          data={courses || []}
          isGlobalFilter
          isPagination
          selectedSortData={selectedSortData}
          setSelectedSortData={setSelectedSortData}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
          pageSize={pageSize}
          setSearchString={setSearchString}
          searchString={searchString}
          SearchPlaceholder="Search courses..."
          pagination="pagination"
          docName="Courses"
          totalrows={totalrows}
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />
      </div>
    </>
  );
};

export default CourseTable;
